import {
  useEffect,
  useState,
  useContext
} from 'react';

import {
  SalesContext
} from 'src/Context/SalesContext';

import {
  DealSortOrders,
  PrepaidFibreUrls,
  SessionsKeys
} from 'src/Common/constants';

import {
  installationAddressNullCheck
} from 'src/Utility/sanitizers';

import {
  getLineSpeeds,
  getOffers,
  getAddressDetails
} from 'src/Services/Services';

import {
  transformTopPicks
} from 'src/Utility/utility';

import {
  tagClearAllFilterClick,
  tagContractTermFilterClick,
  tagOnNoPackagesAvailableForProvider,
  tagOnPrepaidCardClick,
  tagPriceSortFilterClick,
  tagPromotionsOnlyFilterClick,
  tagSpeedFilterClick
} from 'src/Tagging/SalesTagging';

export const useSalesPackages = () => {

  const salesContext = useContext(SalesContext);

  const [ appProactiveSalesFlag, setAppProactiveSalesFlag ] = useState(false);
  const [ miniAppProactiveSalesFlag, setMiniAppProactiveSalesFlag ] = useState(false);
  const [ hasRedirected, setHasRedirected ] = useState(false);

  const contractTerms = [ 'Month to Month', '12 Months', '24 Months' ];
  const sortOptions = [ 'Price - Low to high', 'Price - High to low' ];
  const selectedSpeeds = [];

  const updateSortSelection = (option) => {
    tagPriceSortFilterClick(option);
    salesContext.setSortOrder(option);
  };

  const orderBy = (arr, order = DealSortOrders.ASC) => {
    if (!Array.isArray(arr)) {
      return [];
    }

    return arr.slice().sort((a, b) => {
      if (a.subscriptionType === 'Prepaid' && b.subscriptionType !== 'Prepaid') {
        return -1;
      }
      if (b.subscriptionType === 'Prepaid' && a.subscriptionType !== 'Prepaid') {
        return 1;
      }

      if (a.subscriptionType === 'Prepaid' && b.subscriptionType === 'Prepaid') {
        return 0;
      }

      return order === DealSortOrders.ASC
        ? a?.fromPrice?.value - b?.fromPrice?.value
        : b?.fromPrice?.value - a?.fromPrice?.value;
    });
  };

  const filterByDiscount = (arr) => {
    if (!Array.isArray(arr)) {
      return [];
    }

    const discountedItems = arr.filter(item => {
      if (item.subscriptionType === 'Prepaid') {
        return true;
      }

      return item.onDiscount === true;
    });

    if (discountedItems.length === 0) {
      return arr;
    }

    return discountedItems;
  };

  const checkPromotions = (arr) => {

    if (!Array.isArray(arr)) {

      return false;
    }

    let onDiscount = false;

    arr.map(item => {

      if (item.onDiscount) {

        onDiscount = true;
      }
    });

    return onDiscount;
  };

  const filterBySpeeds = (arr, selectedSpeeds = []) => {
    if (!Array.isArray(arr) || !Array.isArray(selectedSpeeds)) {
      return [];
    }

    return arr.filter(item => {

      if (item.subscriptionType === 'Prepaid') {

        return true;
      }

      return selectedSpeeds.includes(item.download);
    });
  };

  const clearFilters = () => {

    tagClearAllFilterClick('clear all', true);

    const currentFilters = salesContext.filters;

    salesContext.setSelectedContractTerm('Month to Month');
    salesContext.setFilters({
      ...currentFilters,
      selectedSpeeds: [],
      onPromotion: false
    });
  };

  const handlePromotionsCheckboxChange = () => {
    const currentFilters = salesContext.filters;
    tagPromotionsOnlyFilterClick(!currentFilters?.onPromotion);

    salesContext.setFilters({
      ...currentFilters,
      onPromotion: !currentFilters.onPromotion
    });
  };

  const handleTermCheckboxChange = (selectedTerm) => {
    tagContractTermFilterClick(selectedTerm);
    salesContext.setSelectedContractTerm(selectedTerm);
  };

  const handleSpeedCheckboxChange = (speed) => {
    const currentFilters = salesContext.filters;
    const selectedSpeeds = Array.isArray(currentFilters.selectedSpeeds)
      ? currentFilters.selectedSpeeds
      : [];

    const isSelected = selectedSpeeds.includes(speed);

    isSelected ? tagClearAllFilterClick(speed, false) : tagSpeedFilterClick(speed);

    const updatedSpeeds = isSelected
      ? selectedSpeeds.filter(s => s !== speed)
      : [ ...selectedSpeeds, speed ];

    salesContext.setFilters({
      ...currentFilters,
      selectedSpeeds: updatedSpeeds
    });
  };

  const getUniqueDownloadSpeeds = (lineSpeeds) => {

    if (!Array.isArray(lineSpeeds)) {
      return [];
    }

    const downloadSpeeds = lineSpeeds
      .filter(item => item.subscriptionType !== 'Prepaid')
      .map(item => item.download);

    return [ ...new Set(downloadSpeeds) ]?.sort((a, b) => parseInt(a) - parseInt(b));
  };

  const getPlanDetails = async (plan) => {
    const { download, upload } = plan;

    salesContext.setShowSpinner(true);

    try {
      const res = await getOffers(
      salesContext?.installationAddress?.latitude,
      salesContext?.installationAddress?.longitude,
      upload,
      download
      );

      if (res?.result?.contractTerms) {
        salesContext.setPlans(res.result.contractTerms);
      } else {
        console.warn('No contract terms found in response:', res);
        salesContext.setPlans([]);
      }
    } catch (error) {
      console.error('Error fetching plan details:', error);
      salesContext.setGetOffersError(true);
      salesContext.setPlans([]);
    } finally {
      salesContext.setShowSpinner(false);
    }
  };

  const buildInstallationAddress = (sessionData) => {

    if (!sessionData?.address_components) {
      return null;
    }

    const checked = installationAddressNullCheck(sessionData);
    const streetAddress = `${ checked?.street_number?.long_name || '' } ${ checked?.route?.long_name || '' }`;

    return {
      unitNumber: checked?.unit?.long_name || '',
      buildingName: checked?.building?.long_name || '',
      streetNumber: checked?.street_number?.long_name || '',
      streetAddress: `${ streetAddress } ${ checked?.premise?.long_name || '' }`,
      streetName: checked?.route?.long_name || checked?.premise?.long_name || '',
      suburb: checked?.sublocality_level_1?.long_name || checked?.sublocality_level_2?.long_name || '',
      city: checked?.locality?.long_name,
      province: checked?.administrative_area_level_1?.long_name,
      postalCode: checked?.postal_street_code?.long_name || checked?.postal_code?.long_name,
      latitude: `${ sessionData?.geometry?.location?.lat }`,
      longitude: `${ sessionData?.geometry?.location?.lng }`,
      docid: sessionData?.docid,
      seoid: sessionData?.seoid,
      formattedAddress:
      sessionData?.formatted_address ||
      `${ streetAddress }, ${ checked?.sublocality_level_1?.long_name || '' }, ${ checked?.locality?.long_name || '' }, ${ checked?.postal_street_code?.long_name || checked?.postal_code?.long_name || '' }`
    };
  };

  const handlePrepaidRedirect = (installationAddress) => {

    tagOnPrepaidCardClick();

    const url = `${ PrepaidFibreUrls[window.location.hostname] }?address=${ installationAddress.formattedAddress }&lat=${ installationAddress.latitude }&lng=${ installationAddress.longitude }`;

    window.location.replace(url);
  };

  const processLineSpeeds = async (res, installationAddress, salesContext) => {

    const lineSpeeds = res?.result?.linespeeds || [];

    if (salesContext.selectedContractTerm === 'Month to Month') {

      sessionStorage.setItem(SessionsKeys.Products, JSON.stringify(res.result));
    }

    if (lineSpeeds.length === 0) {

      tagOnNoPackagesAvailableForProvider();
      await salesContext.setProviderPackages([]);
      await salesContext.setTopPicks([]);

      return;
    }

    if (lineSpeeds.length === 1 && lineSpeeds[0]?.subscriptionType === 'Prepaid') {

      if (installationAddress) {
        handlePrepaidRedirect(installationAddress);
      }

      return;
    }

    await salesContext.setProviderPackages(lineSpeeds);
    await salesContext.setFilters({
      downloadSpeed: getUniqueDownloadSpeeds(lineSpeeds),
      onPromotion: false
    });

    if (res?.result?.topPicks && Object.keys(res.result.topPicks).length > 0) {
      await salesContext.setTopPicks(transformTopPicks(res.result.topPicks));
    } else {
      await salesContext.setTopPicks([]);
    }
  };

  const loadPackages = async (redirectData, installationAddress) => {

    try {

      if(redirectData) {

        // If there is address, do geocode to get address details.
        if (redirectData?.address) {

          setHasRedirected(true);

          const response = await getAddressDetails(redirectData.address);
          installationAddress = buildInstallationAddress(response?.results[0]);
          salesContext.setInstallationAddress(installationAddress);
        } else{

          tagOnNoPackagesAvailableForProvider();
          salesContext.setProviderPackages([]);
          salesContext.setTopPicks([]);

          return;
        }
      }

      const res = await getLineSpeeds(installationAddress.latitude, installationAddress.longitude, salesContext.selectedContractTerm);

      processLineSpeeds(res, installationAddress, salesContext);

    } catch (err) {
      console.error('Error loading packages:', err);
      tagOnNoPackagesAvailableForProvider();
      salesContext.setProviderPackages([]);
      salesContext.setTopPicks([]);
    }
  };

  useEffect(() => {

    const init = async () => {

      const appFlag = sessionStorage.getItem(SessionsKeys.ProactiveSalesFlag);
      setAppProactiveSalesFlag(appFlag?.toUpperCase() === 'TRUE');

      const miniFlag = sessionStorage.getItem(SessionsKeys.FTTH_MA);
      setMiniAppProactiveSalesFlag(miniFlag?.toUpperCase() === 'TRUE');

      salesContext.setLeadAdditionalServices([]);
      salesContext.setShowSpinner(true);

      const sessionData = JSON.parse(sessionStorage.getItem(SessionsKeys.FibreLeadsAddress));
      const redirectData = JSON.parse(sessionStorage.getItem(SessionsKeys.FibreRedirectAddress));
      const installationAddress = buildInstallationAddress(sessionData);

      // If there is no address, redirect to homepage
      if (!installationAddress && !redirectData) {

        window.location.href = '/vodacom/fibre/sales/leads';

        return;
      }

      salesContext.setInstallationAddress(installationAddress);
      salesContext.setAdditionalVas(null);

      salesContext.setSelectedAdditionalServices({ key: 'superWifi', value: false });
      const storedProducts = JSON.parse(sessionStorage.getItem(SessionsKeys.Products));

      // If there are products in the session storage use them
      if (
        storedProducts &&
        storedProducts.linespeeds &&
        storedProducts.topPicks &&
        salesContext.selectedContractTerm === 'Month to Month'
      ) {

        await processLineSpeeds(
          { result: storedProducts },
          installationAddress,
          salesContext
        );
      } else {
      // Otherwise load packages from API or redirectData lookup
        await loadPackages(redirectData, installationAddress);
      }

      salesContext.setShowSpinner(false);
    };

    init();
  }, [ salesContext.selectedContractTerm ]);

  return {
    updateSortSelection,
    handleTermCheckboxChange,
    handlePromotionsCheckboxChange,
    handleSpeedCheckboxChange,
    getPlanDetails,
    clearFilters,
    orderBy,
    filterByDiscount,
    filterBySpeeds,
    checkPromotions,
    miniAppProactiveSalesFlag,
    appProactiveSalesFlag,
    sortOptions,
    contractTerms,
    selectedSpeeds,
    hasRedirected
  };
};